import React from 'react';
import {
  Box,
  Button,
  Flex,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  UnorderedList,
} from '@chakra-ui/react';
import {useCustomer} from '../../hooks/api/metadata';
import {Agent, getAgentFields} from '../../shared/agent';
import {useDeleteAgent} from '../../hooks/api/agents';
import {
  getSignalEvalFnInputs,
  SignalDefinition,
} from '../../shared/signalDefinition';
import {FieldDefinition, getFieldLabel} from '../../shared/fields/types';

export const DeleteAgentModal = ({
  agent,
  isOpen,
  onClose,
}: {
  agent: Agent;
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <Modal isCentered isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <DeleteAgentModalContent agent={agent} onClose={onClose} />
    </Modal>
  );
};

const DeleteAgentModalContent = ({
  agent,
  onClose,
}: {
  agent: Agent;
  onClose: () => void;
}) => {
  const {id: agentId, label: agentLabel} = agent;
  const {fieldDefinitions, signalDefinitions} = useCustomer();
  const agentFields = getAgentFields({fieldDefinitions, agentId});
  const deleteAgent = useDeleteAgent();

  const inUseFields = agentFields.reduce(
    (acc, field) => {
      const signals = (signalDefinitions ?? []).filter(({evalFn}) => {
        return getSignalEvalFnInputs(evalFn).some(
          (input) => input.type === 'field' && field.id.equals(input.field)
        );
      });

      if (signals.length) {
        acc.push({field, signals});
      }
      return acc;
    },
    [] as {field: FieldDefinition; signals: SignalDefinition[]}[]
  );

  return (
    <ModalContent>
      <ModalCloseButton />
      {inUseFields.length ? (
        <>
          <ModalHeader>Unable to Delete &apos;{agentLabel}&apos;</ModalHeader>
          <ModalBody as={Flex} flexDirection="column" gap="6">
            <Box>
              The fields below are currently being used in the following
              signals:
            </Box>
            {inUseFields.map(({field, signals}) => (
              <Flex key={field.id.toString()} direction="column" gap="2">
                <Box fontWeight="500">{getFieldLabel(field)}</Box>
                <UnorderedList>
                  {signals.map((signal) => (
                    <ListItem key={signal.id.toString()}>
                      {signal.label}
                    </ListItem>
                  ))}
                </UnorderedList>
              </Flex>
            ))}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>OK</Button>
          </ModalFooter>
        </>
      ) : (
        <>
          <ModalHeader>Delete &apos;{agentLabel}&apos;?</ModalHeader>
          <ModalBody>
            Deleting this agent will remove all data associated with it and its
            fields.
          </ModalBody>
          <ModalFooter gap="4">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              colorScheme="red"
              isLoading={deleteAgent.isPending}
              onClick={() =>
                deleteAgent.mutate({agentId}, {onSuccess: onClose})
              }
            >
              Delete
            </Button>
          </ModalFooter>
        </>
      )}
    </ModalContent>
  );
};
